// Kontrola před vložením do košíku (převzato z GEP/Design, upraveno pro samolepky).
// Jen upozorňuje – zákazník může vždy pokračovat. Tiskový soubor se tím nijak nemění.
import type { LabelConfig, IconType } from '../types';

export interface CartWarning { id: string; text: string }

export interface PriceBand { fromSheets: number; pricePerSheet: number }
export interface CartExtras {
  defaultNameValue?: string;
  defaultClassNameValue?: string;
  preCartCheck?: boolean;          // výchozí zapnuto
  minPrintDpi?: number;            // výchozí 150
  priceTable?: PriceBand[];
  priceCurrency?: string;          // výchozí „Kč“
  priceNote?: string;              // např. „vč. DPH, bez dopravy“
}

const MM_PER_INCH = 25.4;

function imageSize(src: string): Promise<{ w: number; h: number } | null> {
  return new Promise(resolve => {
    const img = new Image();
    const t = setTimeout(() => resolve(null), 4000);
    img.onload = () => { clearTimeout(t); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { clearTimeout(t); resolve(null); };
    img.src = src;
  });
}

const isRaster = (s?: string) => typeof s === 'string' && /^data:image\/(png|jpe?g|webp|gif)/i.test(s);

export async function runPreCartCheck(config: LabelConfig, extras: CartExtras): Promise<CartWarning[]> {
  if (extras.preCartCheck === false) return [];
  const warnings: CartWarning[] = [];
  const minDpi = extras.minPrintDpi && extras.minPrintDpi > 0 ? extras.minPrintDpi : 150;

  const visible = Object.entries(config.variantSettings || {}).filter(([, s]) => s && s.visible !== false && (s.customCount ?? 1) > 0);
  if (visible.length === 0) return warnings; // prázdný arch hlídá PreviewPanel podle skutečného počtu

  // Ukázkový nebo prázdný text
  const name = (config.name || '').trim();
  const sampleName = (extras.defaultNameValue || '').trim();
  const ownNames = visible.some(([, s]) => (s.customName || '').trim() && (s.customName || '').trim() !== sampleName);
  if (!ownNames) {
    if (!name) warnings.push({ id: 'name-empty', text: 'Samolepky nemají žádný text (jméno). Pokud ho chcete, doplňte ho.' });
    else if (sampleName && name === sampleName) warnings.push({ id: 'name-sample', text: `Na samolepkách je ukázkový text „${name}“. Přepište ho na vlastní.` });
  }
  const sampleClass = (extras.defaultClassNameValue || '').trim();
  if (sampleClass && (config.className || '').trim() === sampleClass) {
    warnings.push({ id: 'class-sample', text: `Druhý řádek obsahuje ukázkový text „${sampleClass}“.` });
  }

  // Rozlišení vlastních obrázků vůči velikosti samolepky
  const checks: { src: string; mm: number; label: string }[] = [];
  const pushIcon = (icon: IconType | undefined, mm: number) => {
    if (icon && icon.isCustom && isRaster(icon.data as string) && mm > 0) checks.push({ src: icon.data as string, mm, label: 'Vlastní obrázek' });
  };
  for (const [, s] of visible) {
    const w = s.customWidth || 0, h = s.customHeight || 0;
    const side = Math.min(w || h, h || w);
    pushIcon(s.customIcon || config.icon, side * Math.min(1, s.iconScale || 1));
    if (s.fullImageData && isRaster(s.fullImageData)) checks.push({ src: s.fullImageData, mm: Math.max(w, h), label: 'Celoplošný obrázek' });
  }
  const seen = new Map<string, number>();
  for (const c of checks) seen.set(c.src, Math.max(seen.get(c.src) || 0, c.mm));
  let lowShown = false;
  for (const [src, mm] of seen) {
    if (lowShown) break;
    const size = await imageSize(src);
    if (!size) continue;
    const dpi = Math.min(size.w, size.h) / (mm / MM_PER_INCH);
    if (dpi < minDpi) {
      warnings.push({ id: 'low-dpi', text: `Obrázek má nízké rozlišení (asi ${Math.round(dpi)} DPI při velikosti ${Math.round(mm)} mm). V tisku může být neostrý. Lepší je větší soubor nebo vektor (SVG, PDF).` });
      lowShown = true;
    }
  }
  return warnings;
}

// Orientační cena podle počtu archů (pásma „od N archů“ zadaná v administraci)
export function estimatePrice(sheets: number, extras: CartExtras): { total: number; perSheet: number } | null {
  const bands = (extras.priceTable || []).filter(b => b && b.fromSheets > 0 && b.pricePerSheet > 0).sort((a, b) => a.fromSheets - b.fromSheets);
  if (!bands.length || sheets <= 0) return null;
  let band = bands[0];
  for (const b of bands) if (sheets >= b.fromSheets) band = b;
  return { total: Math.round(band.pricePerSheet * sheets * 100) / 100, perSheet: band.pricePerSheet };
}

export const formatMoney = (v: number, currency = 'Kč') =>
  `${v.toLocaleString('cs-CZ', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })} ${currency}`;
